// src/components/LayerImportDropzone.jsx
// Drag-and-drop import UI for KML, KMZ, GeoJSON (Shapefile zip future)
// Wires directly to useUserLayersStore.importLayer(file, name)

import { useRef, useState, useEffect, useCallback } from "react";
import { useUserLayersStore } from "../stores/useUserLayersStore";
import LayerImportDialog from "./layers/LayerImportDialog";

const ACCEPTED_EXTENSIONS = [".kml", ".kmz", ".geojson", ".json", ".zip", ".gpkg"];
const ACCEPTED_MIME = [
    "application/vnd.google-earth.kml+xml",
    "application/vnd.google-earth.kmz",
    "application/geo+json",
    "application/json",
    "application/zip",
    "application/x-zip-compressed",
    "application/gpkg",
];
const MAX_FILE_BYTES = 50 * 1024 * 1024; // 50 MB

function extensionOf(filename) {
    return filename.slice(filename.lastIndexOf(".")).toLowerCase();
}

function isAccepted(file) {
    const ext = extensionOf(file.name);
    return (
        ACCEPTED_EXTENSIONS.includes(ext) || ACCEPTED_MIME.includes(file.type)
    );
}

function defaultName(filename) {
    return filename.replace(/\.[^/.]+$/, "").replace(/[_-]+/g, " ").trim();
}

// ─── States ──────────────────────────────────────────────────────────────────
// idle | dragging | naming | uploading | success | error

export default function LayerImportDropzone({ onDone }) {
    const inspectLayer = useUserLayersStore((s) => s.inspectLayer);
    const importSelectedLayers = useUserLayersStore((s) => s.importSelectedLayers);
    const importing = useUserLayersStore((s) => s.importing);

    const [uiState, setUiState] = useState("idle"); // idle|naming|inspecting|uploading|selecting|success|error
    const [dragOver, setDragOver] = useState(false);
    const [pendingFile, setPendingFile] = useState(null);
    const [layerName, setLayerName] = useState("");
    const [nameError, setNameError] = useState("");
    const [uploadError, setUploadError] = useState("");
    const [progress, setProgress] = useState(0); // 0-100 simulated
    const [pickerData, setPickerData] = useState(null); // { uploadToken, sublayers }

    const fileInputRef = useRef(null);
    const nameInputRef = useRef(null);
    const progressTimerRef = useRef(null);

    // ── Drag handlers ────────────────────────────────────────────────────────

    const handleDragEnter = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(true);
    }, []);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        // Only clear if leaving the zone entirely (not entering a child)
        if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false);
    }, []);

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = "copy";
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(false);
        const file = e.dataTransfer.files?.[0];
        if (!file) return;
        stageFile(file);
    }, []);

    // ── File picker ──────────────────────────────────────────────────────────

    const handleFileChange = useCallback((e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        stageFile(file);
        // Reset input so same file can be re-picked
        e.target.value = "";
    }, []);

    // ── Stage → naming state ─────────────────────────────────────────────────

    function stageFile(file) {
        if (!isAccepted(file)) {
            setUploadError(
                `Unsupported format "${extensionOf(file.name)}". Accepted: KML, KMZ, GeoJSON, ZIP, GPKG.`
            );
            setUiState("error");
            return;
        }
        if (file.size > MAX_FILE_BYTES) {
            setUploadError(
                `File too large (${formatBytes(file.size)}). Maximum size is 50 MB.`
            );
            setUiState("error");
            return;
        }
        setPendingFile(file);
        setLayerName(defaultName(file.name));
        setNameError("");
        setUploadError("");
        setUiState("naming");
        // Focus name input after render
        setTimeout(() => nameInputRef.current?.focus(), 50);
    }

    // ── Upload ───────────────────────────────────────────────────────────────

    async function handleConfirmImport() {
        const trimmed = layerName.trim();
        if (!trimmed) {
            setNameError("Layer name is required.");
            nameInputRef.current?.focus();
            return;
        }
        if (trimmed.length > 80) {
            setNameError("Name must be 80 characters or fewer.");
            return;
        }

        // Phase 1: inspect. If the file has a single selectable sublayer (plain
        // GeoJSON / single-folder KML), import it directly — no dialog. If it has
        // several, open the QGIS-style picker.
        setUiState("inspecting");
        try {
            const { upload_token, sublayers } = await inspectLayer(pendingFile);
            const selectable = (sublayers ?? []).filter((s) => !s.empty);

            if (selectable.length === 0) {
                setUploadError("No importable features found in this file.");
                setUiState("error");
                return;
            }

            if (selectable.length === 1) {
                // Direct import — preserve today's single-name UX.
                startProgress();
                const result = await importSelectedLayers(upload_token, [
                    { index: selectable[0].index, name: trimmed },
                ]);
                finishImport(result);
                return;
            }

            // Multiple sublayers → show the picker.
            setPickerData({ uploadToken: upload_token, sublayers });
            setUiState("selecting");
        } catch (err) {
            setUploadError(err?.message || "Upload failed. Please try again.");
            setUiState("error");
        }
    }

    function startProgress() {
        setUiState("uploading");
        setProgress(0);
        progressTimerRef.current = setInterval(() => {
            setProgress((p) => {
                const next = p + Math.random() * 18;
                return next >= 90 ? 90 : next;
            });
        }, 200);
    }

    // Shared completion for both direct + dialog imports. Reports partial
    // failures rather than swallowing them.
    function finishImport(result) {
        clearInterval(progressTimerRef.current);
        const created = result?.created ?? [];
        const failed = result?.failed ?? [];

        if (created.length === 0) {
            setProgress(0);
            setUploadError(
                failed.length
                    ? `Import failed: ${failed.map((f) => f.name || `#${f.index}`).join(", ")}`
                    : "Import failed. Please try again."
            );
            setUiState("error");
            return;
        }

        setProgress(100);
        setUploadError(
            failed.length
                ? `Imported ${created.length}, but ${failed.length} failed: ${failed.map((f) => f.name || `#${f.index}`).join(", ")}`
                : ""
        );
        setUiState("success");
        setTimeout(() => {
            reset();
            onDone?.();
        }, failed.length ? 2600 : 1200);
    }

    function reset() {
        setUiState("idle");
        setDragOver(false);
        setPendingFile(null);
        setLayerName("");
        setNameError("");
        setUploadError("");
        setProgress(0);
        setPickerData(null);
        clearInterval(progressTimerRef.current);
    }

    function handleKeyDown(e) {
        if (e.key === "Enter") handleConfirmImport();
        if (e.key === "Escape") reset();
    }

    // ── Render ───────────────────────────────────────────────────────────────

    return (
        <div className="flex flex-col gap-2">
            {/* ── Drop Zone (idle / dragging) ── */}
            {(uiState === "idle" || uiState === "dragging") && (
                <div
                    role="button"
                    tabIndex={0}
                    aria-label="Drop a file or click to browse"
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    onKeyDown={(e) => e.key === "Enter" && fileInputRef.current?.click()}
                    className={[
                        "relative flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed px-4 py-5 cursor-pointer select-none transition-all duration-150",
                        dragOver
                            ? "border-accent-info bg-accent-info/10 scale-[1.01]"
                            : "border-slate-700 bg-bg-softer hover:border-slate-600 hover:bg-bg-soft",
                    ].join(" ")}
                >
                    {/* Icon */}
                    <UploadCloudIcon
                        className={`w-7 h-7 transition-colors ${dragOver ? "text-accent-info" : "text-fg-secondary"
                            }`}
                    />
                    <p
                        className={`text-xs font-medium transition-colors ${dragOver ? "text-accent-info" : "text-slate-400"
                            }`}
                    >
                        {dragOver ? "Release to import" : "Drop file or click to browse"}
                    </p>
                    <p className="text-[10px] text-fg-secondary tracking-wide font-mono uppercase">
                        KML · KMZ · GeoJSON · ZIP · GPKG
                    </p>

                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={ACCEPTED_EXTENSIONS.join(",")}
                        className="sr-only"
                        onChange={handleFileChange}
                        tabIndex={-1}
                    />
                </div>
            )}

            {/* ── Name Prompt ── */}
            {uiState === "naming" && (
                <div className="flex flex-col gap-2 rounded-lg border border-slate-700 bg-bg-softer px-3 py-3">
                    {/* File info */}
                    <div className="flex items-center gap-2">
                        <FileIcon className="w-4 h-4 text-accent-info shrink-0" />
                        <span className="text-[11px] text-slate-400 truncate max-w-[180px]">
                            {pendingFile?.name}
                        </span>
                        <span className="ml-auto text-[10px] text-fg-secondary shrink-0">
                            {formatBytes(pendingFile?.size)}
                        </span>
                    </div>

                    {/* Name input */}
                    <div className="flex flex-col gap-1">
                        <label className="text-[10px] text-fg-secondary font-mono uppercase tracking-wider">
                            Layer Name
                        </label>
                        <input
                            ref={nameInputRef}
                            type="text"
                            value={layerName}
                            maxLength={80}
                            onChange={(e) => {
                                setLayerName(e.target.value);
                                if (nameError) setNameError("");
                            }}
                            onKeyDown={handleKeyDown}
                            placeholder="My Layer"
                            className={[
                                "w-full rounded bg-bg px-2.5 py-1.5 text-xs text-white placeholder-slate-600 outline-none transition-all",
                                "border focus:ring-1",
                                nameError
                                    ? "border-red-500/70 focus:ring-red-500/30"
                                    : "border-slate-700 focus:border-accent-info/60 focus:ring-accent-info/20",
                            ].join(" ")}
                        />
                        {nameError && (
                            <p className="text-[10px] text-red-400">{nameError}</p>
                        )}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 pt-0.5">
                        <button
                            onClick={handleConfirmImport}
                            className="flex-1 rounded bg-accent-action/20 px-3 py-1.5 text-xs font-semibold text-accent-action hover:bg-accent-action/30 border border-accent-action/30 transition-colors"
                        >
                            Import
                        </button>
                        <button
                            onClick={reset}
                            className="rounded px-3 py-1.5 text-xs text-fg-secondary hover:text-slate-300 border border-slate-700 hover:border-slate-600 transition-colors"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {/* ── Inspecting ── */}
            {uiState === "inspecting" && (
                <div className="flex items-center gap-2 rounded-lg border border-slate-700 bg-bg-softer px-3 py-3">
                    <div className="h-3.5 w-3.5 rounded-full border-2 border-slate-600 border-t-status-busy animate-spin" />
                    <span className="text-[11px] text-slate-400">Inspecting sublayers…</span>
                </div>
            )}

            {/* ── Sublayer picker (QGIS-style) ── */}
            {uiState === "selecting" && pickerData && (
                <LayerImportDialog
                    sublayers={pickerData.sublayers}
                    uploadToken={pickerData.uploadToken}
                    defaultBaseName={layerName}
                    onImported={finishImport}
                    onClose={reset}
                />
            )}

            {/* ── Uploading ── */}
            {uiState === "uploading" && (
                <div className="flex flex-col gap-2 rounded-lg border border-slate-700 bg-bg-softer px-3 py-3">
                    <div className="flex items-center justify-between">
                        <span className="text-[11px] text-slate-400 truncate max-w-[180px]">
                            {layerName}
                        </span>
                        <span className="text-[10px] text-fg-secondary">
                            {Math.round(progress)}%
                        </span>
                    </div>
                    {/* Progress bar */}
                    <div className="h-1 w-full overflow-hidden rounded-full bg-slate-800">
                        <div
                            className="h-full rounded-full bg-accent-info transition-all duration-200"
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                    <p className="text-[10px] text-fg-secondary">Processing…</p>
                </div>
            )}

            {/* ── Success ── */}
            {uiState === "success" && (
                <div className="flex items-center gap-2 rounded-lg border border-status-ok/30 bg-status-ok/10 px-3 py-2.5">
                    <CheckIcon className="w-4 h-4 text-status-ok shrink-0" />
                    <span className="flex-1 text-xs text-status-ok font-medium">
                        {uploadError || "Layer imported"}
                    </span>
                    <button
                        onClick={reset}
                        className="shrink-0 text-status-ok/60 hover:text-status-ok transition-colors"
                        title="Dismiss"
                    >
                        <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>
            )}

            {/* ── Error ── */}
            {uiState === "error" && (
                <div className="flex flex-col gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2.5">
                    <div className="flex items-start gap-2">
                        <AlertIcon className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                        <p className="text-xs text-red-300">{uploadError}</p>
                    </div>
                    <button
                        onClick={reset}
                        className="self-start text-[10px] text-red-400/70 hover:text-red-300 underline underline-offset-2 transition-colors"
                    >
                        Try again
                    </button>
                </div>
            )}
        </div>
    );
}

// ── Inline icon components (no external dep) ────────────────────────────────

function UploadCloudIcon({ className }) {
    return (
        <svg
            className={className}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <polyline points="16 16 12 12 8 16" />
            <line x1="12" y1="12" x2="12" y2="21" />
            <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
        </svg>
    );
}

function FileIcon({ className }) {
    return (
        <svg
            className={className}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
        </svg>
    );
}

function CheckIcon({ className }) {
    return (
        <svg
            className={className}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <polyline points="20 6 9 17 4 12" />
        </svg>
    );
}

function AlertIcon({ className }) {
    return (
        <svg
            className={className}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
    );
}

function formatBytes(bytes) {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
